/**
 * Shared presentation helpers for feedback (status/type/screen palettes, score
 * colors, value humanising, URL paths). Used by the feedback table and the
 * feedback detail drawer so the two never drift.
 */

export interface StatusStyle {
  label: string;
  bg: string;
  text: string;
  dot: string;
}

export interface TypeStyle {
  label: string;
  bg: string;
  text: string;
}

// Feedback status palette (from the design's FB_STATUS), keyed by status value.
export const STATUS_STYLES: Record<string, StatusStyle> = {
  action: { label: 'Action', bg: '#fff7e6', text: '#925400', dot: '#f59e0b' },
  needs_internal_review: { label: 'Needs Internal Review', bg: '#eff6ff', text: '#1e40af', dot: '#3b82f6' },
  needs_client_review: { label: 'Needs Client Review', bg: '#f5f3ff', text: '#5b21b6', dot: '#8b5cf6' },
  revise: { label: 'Revise', bg: '#fefce8', text: '#713f12', dot: '#eab308' },
  disregard: { label: 'Disregard', bg: '#f9fafb', text: '#6b7280', dot: '#9ca3af' },
  completed: { label: 'Completed', bg: '#e6f7f0', text: '#0f6b45', dot: '#22c37a' },
  request_for_additional_information: { label: 'Req. for More Info', bg: '#ecfeff', text: '#0e7490', dot: '#06b6d4' },
};

export const TYPE_STYLES: Record<string, TypeStyle> = {
  ai_feedback: { label: 'AI', bg: '#eff6ff', text: '#3b82f6' },
  user_feedback: { label: 'User', bg: '#fff7e6', text: '#b45309' },
  stub_feedback: { label: 'Stub', bg: '#f1f5f9', text: '#64748b' },
};

export const SCREEN_COLORS: Record<string, string> = {
  mobile: '#8b5cf6',
  desktop: '#3b82f6',
  tablet: '#06b6d4',
};

/**
 * A [from, to] color pair per feedback category, used for the detail header's
 * 135° gradient. The 12 real categories sit on evenly-spaced hues (30° apart),
 * and every endpoint is tuned to a constant relative luminance so the hues are
 * equally deep — white header text clears WCAG AA (~5:1) on all of them, and no
 * two categories share a color. ("other" is a neutral slate in the same band.)
 * See _gen-cat-colors.mjs for the generator + contrast proof.
 */
export const CATEGORY_GRADIENTS: Record<string, [string, string]> = {
  accessibility: ['#cc3434', '#9f2828'],
  functionality: ['#9a6027', '#774a1e'],
  performance: ['#72721c', '#575716'],
  seo: ['#4c7a1e', '#3b5e17'],
  content: ['#207f20', '#196219'],
  spacing: ['#1f7d4e', '#18603c'],
  responsiveness: ['#1e7a7a', '#175e5e'],
  layout: ['#2d70b4', '#23568a'],
  typography: ['#5f5fd7', '#3f3fcf'],
  component: ['#8f4bd2', '#722eb7'],
  ux: ['#b82eb8', '#8f248f'],
  color: ['#c5317b', '#99265f'],
  other: ['#5f6f84', '#4a5566'],
};

/** Neutral fallback for an unknown or missing category. */
const CATEGORY_GRADIENT_FALLBACK: [string, string] = ['#5f6f84', '#4a5566'];

/** Humanises a value: `needs_review` → `Needs Review`. */
export function humanizeValue(value: string): string {
  return value
    ? value
        .replace(/[_-]+/g, ' ')
        .trim()
        .replace(/\b\w/g, (character) => character.toUpperCase())
    : '';
}

/** Status badge style, with a neutral fallback for unknown statuses. */
export function statusStyle(status: string): StatusStyle {
  return (
    STATUS_STYLES[status] ?? {
      label: humanizeValue(status || 'unknown'),
      bg: '#f1f5f9',
      text: '#475569',
      dot: '#94a3b8',
    }
  );
}

/** Type pill style, with a neutral fallback for unknown types. */
export function typeStyle(type: string): TypeStyle {
  return (
    TYPE_STYLES[type] ?? { label: humanizeValue(type || 'unknown'), bg: '#f1f5f9', text: '#64748b' }
  );
}

export function screenColor(screen: string): string {
  return SCREEN_COLORS[screen] ?? '#6b7280';
}

/** The [from, to] gradient colors for a category, with a neutral fallback. */
export function categoryGradientColors(category: string | null | undefined): [string, string] {
  return CATEGORY_GRADIENTS[category ?? ''] ?? CATEGORY_GRADIENT_FALLBACK;
}

/** A ready-to-use 135° CSS gradient for a category's detail header. */
export function categoryGradient(category: string | null | undefined): string {
  const [from, to] = categoryGradientColors(category);
  return `linear-gradient(135deg, ${from}, ${to})`;
}

/** Score (0–10) formatted for display, dropping a trailing `.0` (7.8, 1, —). */
export function scoreLabel(score: number | null | undefined): string {
  if (score == null) {
    return '—';
  }
  return String(Number(score.toFixed(1)));
}

/** Score color on a 0–10 scale: <5 red, <9 yellow, otherwise green. */
export function scoreColor(score: number | null | undefined): string {
  if (score == null) {
    return 'var(--fb-muted, #6b7280)';
  }
  if (score >= 9) {
    return '#22c37a';
  }
  if (score >= 5) {
    return '#eab308';
  }
  return '#ef4444';
}

/** The path (+ query) of a URL, hiding the scheme and domain. */
export function urlPath(url: string): string {
  if (!url) {
    return '';
  }
  try {
    const parsed = new URL(url);
    return `${parsed.pathname}${parsed.search}` || '/';
  } catch {
    return url;
  }
}

/**
 * A short relative time for conversational timestamps ("Just now", "Yesterday",
 * "3d ago"), matching how the design labels comments. Falls back to a plain date
 * once something is old enough that "42d ago" stops being useful.
 */
export function relativeTime(isoDate: string | null | undefined, now = new Date()): string {
  if (!isoDate) {
    return '';
  }

  const then = new Date(isoDate);
  if (Number.isNaN(then.getTime())) {
    return '';
  }

  const elapsedMinutes = Math.round((now.getTime() - then.getTime()) / 60000);

  // Clock skew between the API and the browser can make a fresh comment look
  // slightly future-dated; read anything within the minute as just now.
  if (elapsedMinutes < 1) {
    return 'Just now';
  }
  if (elapsedMinutes < 60) {
    return `${elapsedMinutes}m ago`;
  }

  const elapsedHours = Math.floor(elapsedMinutes / 60);
  if (elapsedHours < 24) {
    return `${elapsedHours}h ago`;
  }

  const elapsedDays = Math.floor(elapsedHours / 24);
  if (elapsedDays === 1) {
    return 'Yesterday';
  }
  if (elapsedDays < 14) {
    return `${elapsedDays}d ago`;
  }

  return then.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
